import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { CardBoundary } from '@/components/ui/CardBoundary';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { WcNewsSection } from '@/components/news/WcNewsSection';
import { useThemePalette } from '@/config/theme';
import { useMatchNews, useNewsHeadlines } from '@/features/news/hooks';
import type { MatchEventWithRefs, MatchNewsItem, NewsArticle } from '@/lib/data/provider';
import { formatMatchDate, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

interface Scorer {
  playerId: string | null;
  name: string;
  photo: string | null;
  minutes: number[];
  isOwnGoal: boolean;
}

/** Collapse a fixture's goal events into one chip per scorer (with minutes). */
function scorersFor(events: MatchEventWithRefs[]): Scorer[] {
  const order: string[] = [];
  const byKey = new Map<string, Scorer>();
  for (const event of events) {
    if (event.type !== 'goal' && event.type !== 'penalty' && event.type !== 'own_goal') continue;
    const name = event.player?.known_as ?? event.player?.name ?? '?';
    const key = `${event.player?.id ?? name}-${event.type === 'own_goal'}`;
    let scorer = byKey.get(key);
    if (!scorer) {
      scorer = {
        playerId: event.player?.id ?? null,
        name,
        photo: event.player?.photo_url ?? null,
        minutes: [],
        isOwnGoal: event.type === 'own_goal',
      };
      byKey.set(key, scorer);
      order.push(key);
    }
    scorer.minutes.push(event.minute);
  }
  return order.map((key) => byKey.get(key) as Scorer);
}

function NewsCard({ item }: { item: MatchNewsItem }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, arabicNumerals, isRTL } = useLocale();
  const { fixture, events } = item;
  const home = fixture.home_team;
  const away = fixture.away_team;
  const hs = fixture.home_score ?? 0;
  const as = fixture.away_score ?? 0;
  const scorers = scorersFor(events);

  const kickoff = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  if (!kickoff || Number.isNaN(kickoff.getTime())) return null;

  const homeName = home?.short_name ?? home?.name ?? '-';
  const awayName = away?.short_name ?? away?.name ?? '-';
  const h = localizeDigits(String(hs), arabicNumerals);
  const a = localizeDigits(String(as), arabicNumerals);
  // Order matches the (RTL-mirrored) team layout; LRI/PDI lock the segment LTR so
  // the bidi algorithm can't re-flip "0-2" back to "2-0" under the wrong team.
  const score = `⁦${isRTL ? `${a}-${h}` : `${h}-${a}`}⁩`;
  const headline =
    hs === as
      ? t('news.drew', { home: homeName, away: awayName, score })
      : t('news.beat', {
          winner: hs > as ? homeName : awayName,
          loser: hs > as ? awayName : homeName,
          score: hs > as ? score : `⁦${isRTL ? `${h}-${a}` : `${a}-${h}`}⁩`,
        });

  return (
    <CardBoundary>
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/match/${fixture.id}`)}
      className="mx-4 mb-3 active:opacity-90"
    >
      <Card className="overflow-hidden p-0">
        <View className="flex-row items-center justify-between gap-2 border-b border-line bg-surface-2/40 px-4 py-2">
          <AppText variant="micro" weight="bold" className="min-w-0 shrink uppercase tracking-wide text-soft" numberOfLines={1}>
            {fixture.competition?.name ?? ''}
          </AppText>
          <AppText variant="micro" className="shrink-0 text-muted" numberOfLines={1}>
            {formatMatchDate(kickoff, numeralOptions)}
          </AppText>
        </View>

        <View className="flex-row items-center px-4 py-3">
          <View className="flex-1 flex-row items-center gap-2">
            <TeamCrest uri={home?.logo_url} name={homeName} size="sm" />
            <AppText variant="caption" weight="semibold" className="shrink text-ink" numberOfLines={1}>
              {homeName}
            </AppText>
          </View>
          <AppText variant="heading" weight="bold" tabular className="px-3 text-ink">
            {score}
          </AppText>
          <View className="flex-1 flex-row items-center justify-end gap-2">
            <AppText variant="caption" weight="semibold" className="shrink text-end text-ink" numberOfLines={1}>
              {awayName}
            </AppText>
            <TeamCrest uri={away?.logo_url} name={awayName} size="sm" />
          </View>
        </View>

        <View className="gap-2.5 px-4 pb-3.5">
          <AppText variant="caption" className="leading-5 text-soft">
            {headline}
          </AppText>
          {scorers.length > 0 ? (
            <View className="flex-row flex-wrap gap-x-3 gap-y-2">
              {scorers.map((scorer, index) => (
                <View key={index} className="flex-row items-center gap-1.5">
                  <PlayerAvatar uri={scorer.photo} name={scorer.name} size={22} />
                  <AppText variant="micro" weight="semibold" className="text-ink" numberOfLines={1}>
                    {scorer.name}
                    {scorer.isOwnGoal ? ` (${t('news.ownGoal')})` : ''}
                  </AppText>
                  <AppText variant="micro" tabular className="text-volt-bright">
                    {scorer.minutes
                      .sort((a, b) => a - b)
                      .map((minute) => localizeDigits(`${minute}′`, arabicNumerals))
                      .join(', ')}
                  </AppText>
                </View>
              ))}
            </View>
          ) : null}
        </View>
      </Card>
    </Pressable>
    </CardBoundary>
  );
}

/** A real headline from the RSS feeds - taps open the publisher in the browser. */
function HeadlineCard({ article }: { article: NewsArticle }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => {
        // Only follow http(s) links (feed-sourced); never hand the OS a
        // javascript:/intent:/file: scheme.
        if (!/^https?:\/\//i.test(article.url)) return;
        Linking.openURL(article.url).catch(() => {
          // Non-fatal: nothing to do if no browser can open the link.
        });
      }}
      className="mx-4 mb-3 active:opacity-90"
    >
      <Card className="overflow-hidden p-0">
        {article.image_url ? (
          <Image
            source={{ uri: article.image_url }}
            style={{ width: '100%', height: 168 }}
            contentFit="cover"
            transition={150}
            cachePolicy="memory-disk"
          />
        ) : null}
        <View className="gap-2 px-4 py-3.5">
          <AppText variant="caption" weight="bold" className="leading-5 text-ink" numberOfLines={3}>
            {article.title}
          </AppText>
          {article.summary ? (
            <AppText variant="micro" className="leading-5 text-soft" numberOfLines={2}>
              {article.summary}
            </AppText>
          ) : null}
          <View className="flex-row items-center justify-between gap-2 pt-0.5">
            <View className="min-w-0 flex-1 flex-row items-center gap-1.5">
              <View className="h-1.5 w-1.5 rounded-full bg-volt" />
              <AppText variant="micro" weight="bold" className="shrink uppercase tracking-wide text-volt-bright" numberOfLines={1}>
                {article.source}
              </AppText>
              <AppText variant="micro" className="shrink-0 text-muted" numberOfLines={1}>
                · {formatMatchDate(new Date(article.published_at), numeralOptions)}
              </AppText>
            </View>
            <Ionicons name="open-outline" size={14} color={palette.muted} />
          </View>
        </View>
      </Card>
    </Pressable>
  );
}

type NewsTab = 'worldcup' | 'headlines' | 'recaps';

/**
 * News - real football headlines (free Arabic + English RSS feeds, linking out
 * to the publisher) plus an auto, bilingual feed of templated match recaps.
 * Headlines lead; the recaps tab keeps the no-editorial match summaries.
 */
export default function NewsScreen() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [tab, setTab] = useState<NewsTab>('worldcup');

  const headlines = useNewsHeadlines(locale, 30);
  const recaps = useMatchNews(25);
  const articles = headlines.data ?? [];
  const items = recaps.data ?? [];

  return (
    <Screen>
      <BackHeader title={t('news.title')} />
      <View className="px-4 pb-3 pt-1">
        <Segmented
          options={[
            { value: 'worldcup', label: t('news.tabWorldCup') },
            { value: 'headlines', label: t('news.tabHeadlines') },
            { value: 'recaps', label: t('news.tabRecaps') },
          ]}
          value={tab}
          onChange={setTab}
        />
      </View>
      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        {tab === 'worldcup' ? (
          <WcNewsSection limit={30} />
        ) : tab === 'headlines' ? (
          headlines.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-44 w-full" />
              ))}
            </View>
          ) : headlines.isError ? (
            <ErrorState error={headlines.error} onRetry={() => headlines.refetch()} />
          ) : articles.length === 0 ? (
            <EmptyState
              icon="newspaper-outline"
              title={t('news.headlinesEmptyTitle')}
              body={t('news.headlinesEmptyBody')}
            />
          ) : (
            articles.map((article) => <HeadlineCard key={article.id} article={article} />)
          )
        ) : recaps.isPending ? (
          <View className="gap-3 px-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-28 w-full" />
            ))}
          </View>
        ) : recaps.isError ? (
          <ErrorState error={recaps.error} onRetry={() => recaps.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState icon="newspaper-outline" title={t('news.emptyTitle')} body={t('news.emptyBody')} />
        ) : (
          items.map((item) => <NewsCard key={item.fixture.id} item={item} />)
        )}
      </ScrollView>
    </Screen>
  );
}
